import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Upload, 
  FileText, 
  X, 
  Loader2,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TemplateUpload({ user, setUser }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: ""
  });

  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      if (!uploadedFile.name.endsWith('.docx')) {
        toast.error("Seuls les fichiers .docx sont acceptés");
        return;
      }
      setFile(uploadedFile);
      if (!formData.name) {
        setFormData(prev => ({
          ...prev,
          name: uploadedFile.name.replace('.docx', '')
        }));
      }
    }
  }, [formData.name]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    maxFiles: 1
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Le nom du modèle est requis");
      return;
    }

    setUploading(true);

    try {
      const submitData = new FormData();
      submitData.append('file', file);
      submitData.append('name', formData.name);
      if (formData.description) submitData.append('description', formData.description);
      if (formData.category) submitData.append('category', formData.category);

      const response = await fetch(`${API_URL}/api/templates`, {
        method: 'POST',
        credentials: 'include',
        body: submitData
      });

      if (response.ok) {
        const template = await response.json();
        toast.success("Modèle importé avec succès");
        navigate(`/contracts/new/${template.template_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors de l'import");
      }
    } catch (error) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8 max-w-3xl mx-auto" data-testid="template-upload-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
            Importer un modèle
          </h1>
          <p className="text-muted-foreground">
            Importez un document Word (.docx) avec des champs marqués par {"{...}"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* File Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Fichier modèle</CardTitle>
              <CardDescription>
                Format accepté : .docx avec champs marqués
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!file ? (
                <div
                  {...getRootProps()}
                  className={`upload-area ${isDragActive ? 'drag-active' : ''}`}
                  data-testid="dropzone"
                >
                  <input {...getInputProps()} data-testid="file-input" />
                  <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-sm font-medium mb-1">
                    {isDragActive ? "Déposez le fichier ici" : "Glissez-déposez votre fichier Word"}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    ou cliquez pour parcourir
                  </p>
                  <Button type="button" variant="outline" size="sm">
                    Parcourir
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-muted/30" data-testid="uploaded-file">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon"
                    onClick={removeFile}
                    data-testid="remove-file-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Template Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations</CardTitle>
              <CardDescription>
                Décrivez votre modèle de contrat
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom du modèle *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Contrat de travail CDI"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <Input
                  id="category"
                  placeholder="Ex: Ressources Humaines, Commercial..."
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  data-testid="template-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Description du modèle et de son utilisation..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="template-description-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Syntax Help */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Syntaxe des champs</CardTitle>
              <CardDescription>
                Comment marquer les champs dans votre document
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-mono text-sm mb-2">{"{nom_du_champ}"}</p>
                <p className="text-xs text-muted-foreground">
                  Syntaxe avec accolades
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-mono text-sm mb-2">[[BLOC:ASSOCIE]]...[[/BLOC:ASSOCIE]]</p>
                <p className="text-xs text-muted-foreground">
                  Blocs répétables pour les contrats multi-parties
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/templates')}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={!file || uploading}
              data-testid="submit-template-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importer le modèle
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
